import { v4 as uuid } from 'uuid';
import * as deviceModel from '~/models/device';
import * as studioModel from '~/models/studio';
import { assert, assertNever, labelGenerator } from '~/util';
import { parseDevice, parseDeviceChannels } from '~/util/devicesParser';
import { evaluateInt, evaluateNum } from './parameter';
import { importWaveform, waveformsDuration } from './waveform';
import type * as schema from '~/models/pulser-schema';

/**
 * Import experiment data from common serialization format shared with Pulser python library.
 * This import is not fully data preserving, as there are some data transforms that might destroy information.
 * Notably, we do not maintain the present expressions and variables, and bake their results at import time.
 *
 * @param pulserSequence Pulser sequence to import
 * @returns sequence in internal experiment format
 */
export function importFromPulser(
  pulserSequence: schema.PulserSequence
): studioModel.StudioExperiment {
  let dev: schema.Device | string;
  const deviceInfo = pulserSequence.device;

  if (typeof deviceInfo === 'string') {
    dev = deviceInfo;
  } else {
    dev = deviceInfo.name;
  }

  const device: Readonly<studioModel.Device> = parseDevice(
    pulserSequence.device
  );
  const deviceChannels: readonly deviceModel.KnownChannelData[] =
    parseDeviceChannels(pulserSequence.device);

  const selectedDeviceChannels = new Set<number>();
  const channels: studioModel.Channel[] = Object.entries(
    pulserSequence.channels
  ).map(([label, hardwareChannel]): studioModel.Channel => {
    const channelIdx = deviceChannels.findIndex((ch) => {
      const hwChannel = studioModel.getHardwareChannelId(ch);
      return hwChannel === hardwareChannel;
    });
    assert(channelIdx !== -1);
    const channel = deviceChannels[channelIdx];
    selectedDeviceChannels.add(channelIdx);
    return {
      uuid: uuid(),
      label,
      ...channel
    };
  });

  if (dev !== 'MockDevice') {
    const channelLabelGenerator = labelGenerator(
      Object.keys(pulserSequence.channels),
      'ch'
    );
    for (let i = 0; i < deviceChannels.length; i++) {
      if (selectedDeviceChannels.has(i)) {
        continue;
      }
      channels.push({
        uuid: uuid(),
        label: channelLabelGenerator(),
        ...deviceChannels[i]
      });
    }
  }

  // imported channel labels are guaranteed to be unique, so we can map them to uuid one to one
  const channelMap = Object.fromEntries(channels.map((ch) => [ch.label, ch]));

  const register: studioModel.Atom[] = pulserSequence.register.map((a) => ({
    uuid: uuid(),
    label: a.name,
    pos: { x: a.x, y: a.y }
  }));

  const vars = pulserSequence.variables;
  const isAtom = (index: number) => pulserSequence.register.length > index;

  const delayToApply = new Map<string, number>();
  const currentTarget = new Map<string, string>();
  const phaseOffsetToApply = new Map<string, number>();
  /** Get unique map key for given target/basis pair */
  function phaseRefKey(targetUuid: string, basis: studioModel.Basis): string {
    return `${targetUuid}:${basis}`;
  }

  const mockDeviceChannelsWithDetuning = new Set<string>();
  let pulses: studioModel.Pulse[] = [];
  const pulseUuidMap = new Map<string, studioModel.Pulse>();
  const lastChannelPulse = new Map<string, string>();
  const futureDepsPerChannel = new Map<string, string[]>();
  for (const channel of channels) {
    futureDepsPerChannel.set(channel.uuid, []);
  }

  // load pulses
  for (const op of pulserSequence.operations) {
    switch (op.op) {
      case 'delay': {
        const channelUuid = channelMap[op.channel].uuid;
        const delay = evaluateInt(op.time, vars);
        const currentDelay = delayToApply.get(channelUuid) ?? 0;
        delayToApply.set(channelUuid, currentDelay + delay);
        break;
      }
      case 'align': {
        // for every aligned channel, schedule dependencies on latest pulse of other channels for next pulse on given channel
        for (const channel of op.channels) {
          const channelUuid = channelMap[channel].uuid;
          const deps = futureDepsPerChannel.get(channelUuid);
          assert(deps != null);
          for (const depChannel of op.channels) {
            if (channel === depChannel) {
              continue;
            }
            const depPulseUuid = lastChannelPulse.get(
              channelMap[depChannel].uuid
            );
            if (depPulseUuid != null) {
              deps.push(depPulseUuid);
            }
          }
        }
        break;
      }
      case 'target': {
        const channelUuid = channelMap[op.channel].uuid;
        const target = evaluateInt(op.target, vars);
        assert(isAtom(target));
        currentTarget.set(channelUuid, register[target].uuid);
        break;
      }
      case 'pulse': {
        const channel = channelMap[op.channel];
        if (channel == null) {
          throw new Error(`[bug] Channel '${op.channel}' not found`);
        }

        const amplitude = importWaveform(op.amplitude, vars);
        let detuning: studioModel.Waveform[] | null = importWaveform(
          op.detuning,
          vars
        );

        if (
          dev === 'MockDevice' &&
          deviceModel.channelCanConfigureDetuning(channel)
        ) {
          channel.enableDetuning = true;
          mockDeviceChannelsWithDetuning.add(channel.uuid);
        } else if (!channel.enableDetuning) {
          detuning = null;
        } else if (detuning.length === 0) {
          detuning = [
            {
              kind: 'constant',
              constraint: {
                kind: 'area',
                maxVal: 0,
                duration: waveformsDuration(amplitude)
              }
            }
          ];
        }

        const targetUuid = currentTarget.get(channel.uuid) ?? null;
        const basis = channel.basis;
        const bakedPhaseShift = targetUuid
          ? (phaseOffsetToApply.get(phaseRefKey(targetUuid, basis)) ?? 0)
          : 0;

        const dependencies = [
          ...(futureDepsPerChannel.get(channel.uuid) ?? [])
        ];
        if (dependencies.length > 0) {
          futureDepsPerChannel.set(channel.uuid, []);
        }
        const delay = delayToApply.get(channel.uuid) ?? 0;
        if (delay > 0) {
          delayToApply.set(channel.uuid, 0);
        }

        // remove dependencies from wait-for-all pulse, as they are implicit anyway
        if (op.protocol === 'wait-for-all') {
          dependencies.length = 0;
        }

        const pulse: studioModel.Pulse = {
          uuid: uuid(),
          targetUuid,
          channelUuid: channel.uuid,
          protocol: op.protocol,
          dependencies,
          delay,
          amplitude,
          detuning,
          phase: bakedPhaseShift + evaluateNum(op.phase, vars),
          postPhaseShift:
            bakedPhaseShift + evaluateNum(op.post_phase_shift, vars)
        };

        pulseUuidMap.set(pulse.uuid, pulse);
        lastChannelPulse.set(pulse.channelUuid, pulse.uuid);
        pulses.push(pulse);
        break;
      }
      case 'phase_shift': {
        // phase shift op is not directly supported,
        // instead it's baked into the next relevant pulse's phase and postPhaseShift

        const basis = studioModel.importBasis(op.basis as schema.Basis);
        const phaseOffset = evaluateNum(op.phi, vars);
        const targets = op.targets.map((t) => {
          const target = evaluateInt(t, vars);
          assert(isAtom(target));
          return register[target].uuid;
        });

        for (const target of targets) {
          const key = phaseRefKey(target, basis);
          const currentOffset = phaseOffsetToApply.get(key) ?? 0;
          phaseOffsetToApply.set(key, currentOffset + phaseOffset);
        }
        break;
      }
      case 'enable_eom_mode':
      case 'add_eom_pulse':
      case 'disable_eom_mode':
      case 'modify_eom_setpoint':
      case 'pulse_arbitrary_phase':
      case 'config_slm_mask':
      case 'config_detuning_map':
      case 'add_dmm_detuning':
      case 'truncate':
        throw new Error(`Operation '${op.op}' is not supported`);
      default:
        assertNever(op);
    }
  }

  if (dev === 'MockDevice' && mockDeviceChannelsWithDetuning.size !== 0) {
    pulses = pulses.map((pulse) => {
      let detuning = pulse.detuning;
      if (
        mockDeviceChannelsWithDetuning.has(pulse.channelUuid) &&
        (detuning === null || detuning.length === 0)
      ) {
        detuning = [
          {
            kind: 'constant',
            constraint: {
              kind: 'area',
              maxVal: 0,
              duration: waveformsDuration(pulse.amplitude)
            }
          }
        ];
      }
      return { ...pulse, detuning };
    });
  }

  return {
    name: pulserSequence.name,
    device: device,
    register,
    channels,
    pulses,
    basis:
      pulserSequence.measurement != null
        ? studioModel.importBasis(pulserSequence.measurement)
        : null,
    metadata: pulserSequence.metadata
  };
}
